import React, { useCallback, useEffect, useState } from "react";
import { Button, Select, Checkbox, Radio } from "antd";
import { CategoriesSpecsService } from "../../../../services/category_specs.service";
import {
  SpecOption,
  SpecsDataType,
} from "../../../../interfaces/admin.interface";

interface SelectProps {
  visible: number;
  setFieldValue: (field: string, value: any) => void;
  specs: SpecOption[];
}

const SelectOptions: React.FC<SelectProps> = ({
  visible,
  setFieldValue,
  specs,
}) => {
  const [loading, setLoading] = useState(true);
  const [specsData, setSpecsData] = useState<SpecsDataType[]>([]);
  const [selects, setSelects] = useState<
    { id: number; spec_id: number | null }[]
  >([{ id: 0, spec_id: null }]);
  const [selectedOptions, setSelectedOptions] = useState<
    Record<number, number[]>
  >({});

  const resetSelections = useCallback(() => {
    setSelects([{ id: 0, spec_id: null }]);
    setSelectedOptions({});
    setFieldValue("specs", []);
  }, [setFieldValue]);
  useEffect(() => {
    if (visible) resetSelections();
  }, [visible, resetSelections]);

  useEffect(() => {
    if (specs.length > 0) {
      const uniqueSpecs = specs.reduce<{ id: number; spec_id: number }[]>(
        (acc, curr, index) => {
          if (!acc.find((s) => s.spec_id === curr.spec_id)) {
            acc.push({ id: index, spec_id: curr.spec_id });
          }
          return acc;
        },
        []
      );

      setSelects(uniqueSpecs);
      const formattedOptions: Record<number, number[]> = {};
      specs.forEach((spec) => {
        if (!formattedOptions[spec.spec_id]) {
          formattedOptions[spec.spec_id] = [];
        }
        formattedOptions[spec.spec_id].push(spec.option_id);
      });

      setSelectedOptions(formattedOptions);
    }
  }, [specs]);

  useEffect(() => {
    setLoading(true);
    CategoriesSpecsService.getAllCategoriesSpecs()
      .then((response) => setSpecsData(response?.data?.data || []))
      .finally(() => setLoading(false));
  }, []);

  const addSelect = () => {
    setSelects((prev) => [...prev, { id: prev.length + 1, spec_id: null }]);
  };

  const removeSelect = (id: number) => {
    setSelects((prev) => prev.filter((select) => select.id !== id));

    setSelectedOptions((prev) => {
      const updated = { ...prev };
      const removedSpecId = selects.find((s) => s.id === id)?.spec_id;

      if (removedSpecId) {
        delete updated[removedSpecId];
      }

      return updated;
    });

    setFieldValue(
      "specs",
      (prevSpecs: { spec_id: number; option_id: number }[]) =>
        prevSpecs.filter(
          (s) => s.spec_id !== selects.find((s) => s.id === id)?.spec_id
        )
    );
  };

  const handleSpecChange = (id: number, newSpecId: number | null) => {
    setSelects((prev) =>
      prev.map((s) => (s.id === id ? { ...s, spec_id: newSpecId } : s))
    );
  };

  const handleOptionChange = (
    selectId: number,
    spec_id: number,
    optionId: number,
    checked: boolean,
    isRadio: boolean
  ) => {
    setSelectedOptions((prev) => {
      const updated = { ...prev };
      if (!updated[spec_id]) {
        updated[spec_id] = [];
      }

      if (isRadio) {
        updated[spec_id] = [optionId];
      } else {
        if (checked) {
          updated[spec_id].push(optionId);
        } else {
          updated[spec_id] = updated[spec_id].filter((id) => id !== optionId);
        }
      }

      return updated;
    });
  };

  useEffect(() => {
    const formattedSpecs = Object.entries(selectedOptions).flatMap(
      ([specId, optionIds]) =>
        optionIds.map((optionId) => ({
          spec_id: Number(specId),
          option_id: optionId,
        }))
    );
    setFieldValue("specs", formattedSpecs);
  }, [selectedOptions, setFieldValue]);

  return (
    <>
      <p className="select_title">Məhsul üçün xüsusiyyətlər seçin</p>
      {selects.map(({ id, spec_id }, idx) => {
        const selectedSpec = specsData.find((s) => s.id === spec_id);
        const isRadio = selectedSpec?.price_mode;
        return (
          <div key={idx} className="specs_container">
            <div className="specs">
              <Select
                style={{ width: "50%", height: "46px" }}
                placeholder={
                  loading ? "Xüsusiyyətlər yüklənir..." : "Xüsusiyyətləri seçin"
                }
                options={specsData.map((element) => ({
                  label: element.name,
                  value: element.id,
                }))}
                value={spec_id}
                onChange={(value) => handleSpecChange(id, value)}
              />
              {idx === 0 && (
                <div className="add_specs" onClick={addSelect}>
                  <i className="fa-solid fa-plus"></i>
                </div>
              )}
              {idx > 0 && (
                <div
                  className="add_specs remove_specs"
                  onClick={() => removeSelect(id)}
                >
                  <i className="fa-solid fa-minus"></i>
                </div>
              )}
              {idx === 0 && (
                <Button
                  onClick={resetSelections}
                  type="default"
                  style={{ height: "46px" }}
                >
                  Sıfırla
                </Button>
              )}
            </div>
            <div className="checkboxes">
              {spec_id &&
                selectedSpec?.options?.map((option) => (
                  <div key={option.id} className="checkbox_">
                    {isRadio ? (
                      <Radio
                        checked={selectedOptions[spec_id]?.includes(
                          Number(option.id)
                        )}
                        onChange={(e) =>
                          handleOptionChange(
                            id,
                            spec_id,
                            Number(option.id),
                            e.target.checked,
                            true
                          )
                        }
                      >
                        {option.text}
                      </Radio>
                    ) : (
                      <Checkbox
                        checked={selectedOptions[spec_id]?.includes(
                          Number(option.id)
                        )}
                        onChange={(e) =>
                          handleOptionChange(
                            id,
                            spec_id,
                            Number(option.id),
                            e.target.checked,
                            false
                          )
                        }
                      >
                        {option.text}
                      </Checkbox>
                    )}
                  </div>
                ))}
            </div>
          </div>
        );
      })}
    </>
  );
};

export default SelectOptions;
